import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { css, cx, etiColors, resetButtonStyle } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { LoadingDots, Icon } from '@eti/components';
import { price as priceUtils } from '@eti/utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const priceWrapperStyles = css`
  align-items: flex-end;
  color: ${etiColors.grayScale[10]};
  display: flex;
  flex-direction: column;
`;
const getColor = isHighlighted => isHighlighted ? etiColors.confirmation.solid : etiColors.black;
const priceStyles = isHighlighted => css`
  color: ${getColor(isHighlighted)};
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  transition: 0.4s;
`;
const textStyles = error => css`
  font-size: 0.75rem;
  margin-bottom: 4px;
  ${error && `color: ${etiColors.critical.solid};`};
`;
const containerStyles = css`
  background-color: ${etiColors.white};
  border-radius: 12px;
  margin-top: 10px;
  padding: 0 18px;
`;
export const noPriceValue = '---';
const getPriceText = (price, error) => {
  if (error) {
    return 'Manulife.Traveler.Price.Error';
  }
  if (price === null) {
    return 'Manulife.Traveler.No.Price.Available';
  }
  if (Number.isInteger(price)) {
    return 'Manulife.Traveler.Price.Text';
  }
  return 'Manulife.Traveler.No.Price.Text';
};
const styles = css`
  margin-right: 10px;

  [dir='rtl'] & {
    margin-left: 10px;
    margin-right: 0;
  }
`;
const buttonStyles = css`
  ${resetButtonStyle}; /* stylelint-disable-next-line */
  align-items: center;
  background: ${etiColors.white};
  border: 1px solid ${etiColors.info.solid};
  border-radius: 3px;
  color: ${etiColors.info.solid};
  display: flex;
  font-size: 0.625rem;
  padding: 5px 20px 7px;
`;
const RefetchButton = _ref => {
  let {
    children,
    onClick
  } = _ref;
  return /*#__PURE__*/_jsxs("button", {
    className: buttonStyles,
    onClick: onClick,
    type: "button",
    children: [/*#__PURE__*/_jsx(Icon, {
      className: styles,
      color: etiColors.info.solid,
      id: "reload",
      size: "14px "
    }), /*#__PURE__*/_jsx("span", {
      children: children
    })]
  });
};
RefetchButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func
};
const ManulifeTravelerPrice = _ref2 => {
  let {
    className,
    error,
    isSelected,
    isLoading,
    price = noPriceValue,
    onRefetch
  } = _ref2;
  const {
    t
  } = useTranslation();
  const [isHighlighted, setIsHighlighted] = useState(isSelected);
  const hasPrice = Number.isInteger(price);
  const formattedPrice = hasPrice ? priceUtils.formatPrice(price) : price;
  const notAvailablePrice = price === null;
  useEffect(() => {
    let timeout;
    if (!isLoading) {
      if (isSelected) {
        setIsHighlighted(true);
        timeout = setTimeout(() => {
          setIsHighlighted(false);
        }, 1000);
      } else {
        setIsHighlighted(false);
        clearTimeout(timeout);
      }
    }
    return () => clearTimeout(timeout);
  }, [isSelected, price, setIsHighlighted, isLoading]);
  return /*#__PURE__*/_jsx("div", {
    className: cx(priceWrapperStyles, className),
    children: isLoading ? /*#__PURE__*/_jsx(LoadingDots, {
      containerStyle: containerStyles,
      dataTestId: "manulife-loading-price"
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("span", {
        className: textStyles(error || notAvailablePrice),
        children: t(getPriceText(price, error))
      }), error ? /*#__PURE__*/_jsx(RefetchButton, {
        onClick: onRefetch,
        children: t('Manulife.Traveler.Refetch.Price')
      }) : /*#__PURE__*/_jsx("span", {
        className: priceStyles(isHighlighted),
        children: formattedPrice
      })]
    })
  });
};
ManulifeTravelerPrice.propTypes = {
  className: PropTypes.string,
  error: PropTypes.node,
  isLoading: PropTypes.bool,
  isSelected: PropTypes.bool,
  onRefetch: PropTypes.func,
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
export default ManulifeTravelerPrice;
//# sourceMappingURL=ManulifeTravelerPrice.js.map